<template>
  <v-layout :column="loads.length || loadingBookedLoads">
    <v-flex v-if="!appLoading && !loadingBookedLoads && !loads.length">
      <empty-state :message="`You don't have any ${statusName} loads.`" />
    </v-flex>
    <template v-else>
      <div v-if="isMobile">
        <v-card>
          <v-card-title class="py-0">
            <v-layout column>
              <label>
                {{ loadsAccurateSince }}
              </label>
            </v-layout>

            <v-btn fab small flat right
                   color="accent"
                   @click="$emit('refresh')">
              <v-icon>refresh</v-icon>
            </v-btn>
          </v-card-title>
        </v-card>

        <v-card v-for="load in loads" :key="load.id" class="xs12 my-2">

          <v-card-title>
            <v-layout column>
              <h6 class="title">
                {{load.humanId}}
              </h6>
            </v-layout>

            <v-btn block color="accent" @click="viewLoadDetail(load)">
              <v-icon size="18" color="white">visibility</v-icon>
              <span class="pl-2">View Load</span>
            </v-btn>
          </v-card-title>

          <v-card-text>
            <b>From:</b> {{loadDetails(load) | originName}}<br/>
            <b>To:</b> {{loadDetails(load) | destinationName}}<br/><br/>

            <p>
              <b>Pickup Time:</b>
              {{loadModel.notificationPickupTime(load).value }}
              <span class="font-italic" v-if="status === loadStatus.SCHEDULED">
            ({{ loadModel.notificationPickupTime(load).providedBy }})
          </span>
              <span v-else>
            {{ loadModel.notificationDeliveryTime(load).value }}
          </span>
            </p>

            <p>
              <load-distance :load-details="load"></load-distance>
            </p>

            <p>
              <b>Carrier:</b>
              {{bookedLoadHaulingCompany(load).name}}
              <span class="font-italic" v-if="driverId(load)">
            {{ driverName(load) }}
          </span>
              <span class="font-italic" v-else>(no driver assigned)</span>
            </p>
            <v-select-load-driver :load="load"
                                  @refresh="$emit('updateLoad', $event)"
                                  :company="company">
            </v-select-load-driver>

            <span v-if="loadModel.requiresWashout(load)">
            <icon-base width="25" height="25" icon-color="#352208" icon-name="hose">
              <icon-hose />
            </icon-base>
        </span>
            <span v-if="loadModel.requireWoodChips(load)">
          <icon-base width="25" height="25" icon-color="#352208"
                     viewBox="0 0 5 5" icon-name="wood">
            <icon-wood />
          </icon-base>
        </span>
            <span v-if="loadModel.requireWeatherboards(load)">
              <icon-base width="25" height="25" icon-name="cold" icon-color="#352208">
                <icon-cold />
              </icon-base>
            </span>
          </v-card-text>

        </v-card>
      </div>
      <template v-else>
        <v-container fluid class="pa-0">
          <v-layout :row="!isMobile" :column="isMobile">
            <v-flex xs12 md3 v-if="isMobile? !selectedLoad : true">
              <v-load-nav-list :search-prompt="`Search ${statusName} loads`"
                               :title="`${statusName} (${loads.length})`"
                               :accurate-since="loadsAccurateSince"
                               :loads="filteredLoads"
                               :customerLoads="groupedBySponsorLoads"
                               :carrierLoads="groupedByCarrierLoads"
                               :loading="loadingBookedLoads"
                               :history="false"

                               @update:searchTerm="search = $event"
                               @refresh="refreshBookedLoads">
                <template slot="subheader">
                  <v-subheader class="sort-btn" @click="toggleSort()">
                    <v-icon class="pr-2" color="primary">
                      {{ status === loadStatus.SCHEDULED ? 'arrow_upward' : 'play_for_work' }}
                    </v-icon>
                    <span v-if="status === loadStatus.SCHEDULED">Pickup Time</span>
                    <span v-else>Delivery Time</span>

                    <v-spacer/>
                    <v-icon color="accent" class="pl-3">
                      event
                    </v-icon>
                    <v-icon v-if="sortDesc"
                            color="accent">
                      arrow_drop_down
                    </v-icon>
                    <v-icon v-else
                            color="accent">
                      arrow_drop_up
                    </v-icon>
                  </v-subheader>
                </template>

                <template slot="loadTile"
                          slot-scope="{ idx, load }">
                  <v-list-tile :key="`unassigned_load_${idx}_${load.listingHumanId}`"
                               @click="selectLoad(load)"
                               :class="[{'primary lighten-1': isSelectedLoad(load)}, loadStyleClass(load)]"
                               :inactive="isSelectedLoad(load)"
                               ripple avatar>

                    <v-list-tile-content>
                      <v-list-tile-title>{{  loadModel.loadNickname(load) || _.get(load, 'sponsor.name', '...') }}</v-list-tile-title>
                      <v-list-tile-sub-title class="text--primary">
                        {{ load.humanId }}: {{ load.pickupTime | formatDay }}
                      </v-list-tile-sub-title>
                      <v-list-tile-sub-title>
                        {{bookedLoadHaulingCompany(load).name}}<span v-if="load.tacticalInfo.assignedTruckingCompanyDriverName">, {{load.tacticalInfo.assignedTruckingCompanyDriverName}}</span>
                        <span v-if="_.get(load, 'terms.customerSettlement', false)">, ${{ load.terms.customerSettlement.amount }}</span>
                      </v-list-tile-sub-title>
                    </v-list-tile-content>

                  </v-list-tile>
                  <v-divider :key="`unassigned_divider_${idx}_${load.listingHumanId}`"/>
                </template>

                <template slot="customerLoadsTile" slot-scope="{ idx, loads, sponsor }">
                  <v-list-group :key="`groupedBy_${idx}`">
                    <v-list-tile slot="activator">
                      <v-list-tile-action>
                        <v-chip color="green"
                                text-color="white" small>
                          {{ loads.length }}
                        </v-chip>
                      </v-list-tile-action>
                      <v-list-tile-title>{{ sponsor }}</v-list-tile-title>
                    </v-list-tile>
                    <template v-for="load in loads" >
                      <v-list-tile :key="`grouped_load_${load.humanId}`"
                                   @click="selectLoad(load)"
                                   :class="{'primary lighten-1': isSelectedLoad(load)}"
                                   :inactive="isSelectedLoad(load)"
                                   ripple avatar>
                      <v-list-tile-content>
                        <v-list-tile-title v-if="loadModel.loadNickname(load)">{{ loadModel.loadNickname(load)  }}</v-list-tile-title>
                        <v-list-tile-sub-title class="text--primary">
                          {{ load.humanId }}: {{ load.pickupTime | formatDay }}
                        </v-list-tile-sub-title>
                        <v-list-tile-sub-title>
                          {{bookedLoadHaulingCompany(load).name}}<span v-if="load.tacticalInfo.assignedTruckingCompanyDriverName">, {{load.tacticalInfo.assignedTruckingCompanyDriverName}}</span>
                          <span v-if="_.get(load, 'terms.customerSettlement', false)">, ${{ load.terms.customerSettlement.amount }}</span>
                        </v-list-tile-sub-title>
                      </v-list-tile-content>

                      </v-list-tile>
                      <v-divider :key="`load_divider_${load.humanId}`"/>
                    </template>
                  </v-list-group>
                </template>

                <template slot="carrierLoadsTile" slot-scope="{ idx, loads, carrier }">
                  <v-list-group :key="`groupedBy_${idx}`">
                    <v-list-tile slot="activator">
                      <v-list-tile-action>
                        <v-chip color="accent"
                                text-color="white" small>
                          {{ loads.length }}
                        </v-chip>
                      </v-list-tile-action>
                      <v-list-tile-title>{{ carrier }}</v-list-tile-title>
                    </v-list-tile>
                    <template v-for="load in loads" >
                      <v-list-tile :key="`grouped_load_${load.humanId}`"
                                   @click="selectLoad(load)"
                                   :class="{'primary lighten-1': isSelectedLoad(load)}"
                                   :inactive="isSelectedLoad(load)"
                                   ripple avatar>

                        <v-list-tile-content>
                          <v-list-tile-title v-if="loadModel.loadNickname(load)">
                            {{ loadModel.loadNickname(load) }}
                          </v-list-tile-title>
                          <v-list-tile-sub-title class="text--primary">
                            {{ load.humanId }}: {{ load.pickupTime | formatDay }}
                          </v-list-tile-sub-title>
                          <v-list-tile-sub-title>
                            {{loadModel.loadNickname(load) || load.sponsor.name}}
                            <span v-if="load.tacticalInfo.assignedTruckingCompanyDriverName">, {{load.tacticalInfo.assignedTruckingCompanyDriverName}}</span>
                            <span v-if="_.get(load, 'terms.customerSettlement', false)">, ${{ load.terms.customerSettlement.amount }}</span>
                          </v-list-tile-sub-title>
                        </v-list-tile-content>

                      </v-list-tile>
                      <v-divider :key="`load_divider_${load.humanId}`"/>
                    </template>
                  </v-list-group>
                </template>
              </v-load-nav-list>
            </v-flex>

            <v-flex md9 v-if="!isMobile || viewedLoad">
              <load-detail :currentLoad="selectedLoad" v-if="selectedLoad"
                           class="pl-2"
                           :company="company"
                           :loading="loadingLoad"
                           @removeLoad="removeLoad($event)"
                           @updateLoad="$emit('updateLoad', $event)"
                           @refreshLoad="refreshSelectedLoad()">
              </load-detail>
            </v-flex>
          </v-layout>
        </v-container>
      </template>
    </template>
  </v-layout>
</template>

<script>
import _ from 'lodash';
import API from '@/api';
import moment from 'moment';
import { mapState, mapGetters } from 'vuex';
import Model from '@/types/models/';
import companyNamesMixin from '@/mixins/companyNames';
import IconBase from '@/components/IconBase';
import EmptyState from '@/components/EmptyState';
import IconCold from '@/components/icons/IconCold';
import IconHose from '@/components/icons/IconHose';
import IconWood from '@/components/icons/IconWood';
import IconPdf from '@/components/icons/IconPDF';
import VLoadNavList from '@/components/dashboard/simple/LoadNavList';
import LoadDetail from '@/components/dashboard/simple/LoadDetail';
import * as Checks from '@/store/permissions';
import * as Loads from '@/types/models/load';
import * as types from '@/store/mutationTypes';
import LoadDistance from '@/components/InfoWidgets/LoadDistance';
import VSelectLoadDriver from '@/components/SelectLoadDriver';
import UpdateEtaDialog from '@/components/modals/UpdateEtaDialog';
import refreshPageMixin from '@/mixins/refreshPageMixin';
import loadsRoutingMixin from '@/mixins/loadsRoutingMixin';
import bookedLoadMixin from '@/mixins/bookedLoadMixin';
import { loadStatus } from '@/types';

export default {
  name: 'ScheduledLoads',
  mixins: [bookedLoadMixin, companyNamesMixin, refreshPageMixin, loadsRoutingMixin],
  components: {
    UpdateEtaDialog,
    VSelectLoadDriver,
    LoadDistance,
    EmptyState,
    IconBase,
    IconHose,
    IconWood,
    IconCold,
    IconPdf,
    VLoadNavList,
    LoadDetail,
  },
  props: {
    isActiveSection: {
      type: Boolean,
      default() {
        return false;
      },
    },
    company: {
      type: Object,
      default() {
        return {};
      },
    },
    loadingList: {
      type: Boolean,
      default() {
        return false;
      },
    },
    disabledRouting: {
      type: Boolean,
      default() {
        return false;
      },
    },
    loads: {
      required: true,
      type: Array,
    },
    status: {
      type: String,
      default: null,
    },
    viewedLoad: {
      type: Object,
      default: null,
    },
    dispatched: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    loadsAssignedAt: null,
    sortDesc: true,
    selectedLoad: null,
    checkinBtnLoading: false,
    loadingProfile: false,
    search: '',
    loadingLoad: false,
    pagination: {
      sortBy: null,
      descending: true,
    },
  }),
  computed: {
    ...mapState('applications', {
      appLoading: 'loading',
    }),
    ...mapState('profiles', {
      userProfile: 'userProfile',
    }),
    ...mapGetters('profiles', {
      isLtnAdmin: 'isLtnAdmin',
    }),
    ...mapGetters('bookedLoads', {
      bookedLoadsTime: 'timestamp',
      loadingLoads: 'loadingLoads',
    }),
    loadingBookedLoads() {
      return this.loadingLoads || this.loadingList;
    },
    _() {
      return _;
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    selectedLoadId() {
      return this.bookedLoadId; // from the load routes mixin
    },
    loadStatus() {
      return loadStatus;
    },
    statusName() {
      let name;
      switch (this.status) {
        case loadStatus.IN_TRANSIT:
          name = 'In transit';
          break;
        case loadStatus.SCHEDULED:
          name = 'Scheduled';
          break;
        case loadStatus.DELIVERED:
          name = 'Delivered';
          break;
        default:
          name = '';
      }
      return name;
    },
    groupedBySponsorLoads() {
      const nameSorted = _.sortBy(this.filteredLoads, load => _.get(load, 'sponsor.name', ''));
      return _.groupBy(nameSorted, load => _.get(load, 'sponsor.name', ''));
    },
    groupedByCarrierLoads() {
      const nameSorted = _.sortBy(this.filteredLoads, load => _.get(this.bookedLoadHaulingCompany(load), 'name', ''));
      return _.groupBy(nameSorted, load => _.get(this.bookedLoadHaulingCompany(load), 'name', ''));
    },
    loadsAccurateSince() {
      let since = this.bookedLoadsTime;
      if (this.dispatched) {
        since = this.loadsAssignedAt;
      }

      if (this.isLoadingBookedLoads || since === null) {
        return 'Loading ...';
      }

      return `Updated ${this.$options.filters.formatTimeStamp(since)}`;
    },
    filteredLoads() {
      const filtered = this.filterLoads(this.loads, this.search, this.filter);

      const sort = this.sortDesc ? 'desc' : 'asc';
      if (this.status === loadStatus.SCHEDULED) {
        return _.orderBy(filtered, [load =>
          moment(load.pickupTime, moment.ISO_8601).unix()], [sort]);
      }
      return _.orderBy(filtered, [load =>
        moment(load.deliveryTime, moment.ISO_8601).unix()], [sort]);
    },
    loadModel() {
      return Model.load;
    },
  },
  methods: {
    ...mapGetters('profiles', {
      findCompanyAccount: 'findCompanyAccount',
    }),
    updateSelectedLoadById(list, loadId) {
      if (list && list.length && loadId) {
        // Update selection
        const selectedIndex = _.findIndex(list, load => load.humanId === loadId);
        this.selectLoad(selectedIndex > -1 ? list[selectedIndex] : null);
      }
    },
    selectLoad(load) {
      if (this.isMobile && load) {
        this.$router.push({
          name: 'load.detail',
          params: {
            id: load.humanId,
          },
        });
      } else {
        this.selectedLoad = load;
      }
    },
    async refreshSelectedLoad() {
      this.loadingLoad = true;

      try {
        const apiResponse = await API.load.getLoad({ loadId: this.selectedLoad.humanId });
        this.$store.dispatch('bookedLoads/updateLoad', apiResponse.data);

        const updated = apiResponse.data;
        this.replaceBookedLoad(updated);

        if (_.get(this.selectedLoad, 'loadStatus') !== _.get(updated, 'loadStatus')) {
          this.selectedLoad = null;
        } else {
          this.selectedLoad = updated;
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingLoad = false;
      }
    },
    onRefresh() {
      if (this.selectedLoad) {
        this.refreshSelectedLoad();
      }
    },
    removeLoad(humanId) {
      this.$store.dispatch('bookedLoads/removeLoad', humanId);
      this.selectedLoad = null;
      this.$emit('removeLoad', humanId);
      this.refreshPlannedLoads();
    },
    isSelectedLoad(load) {
      return load.humanId === _.get(this.selectedLoad, 'humanId', null);
    },
    toggleSort() {
      this.sortDesc = !this.sortDesc;
    },
    refreshBookedLoads() {
      if (this.dispatched) {
        this.loadsAssignedAt = null;
        this.$emit('refresh');
      } else {
        this.$store.dispatch('bookedLoads/getBookedLoads', this.company.id);
      }
    },
    refreshPlannedLoads() {
      this.$store.dispatch('plannedLoads/getPlannedLoads', this.company.id);
    },
    normalizeLoads(loads) {
      _.forEach(loads, (load) => {
        // eslint-disable-next-line no-param-reassign
        load.sponsor = load.tacticalInfo.loadDetails.customerCompany || load.tacticalInfo.loadDetails.listingCompany;
        // eslint-disable-next-line no-param-reassign
        load.pickupTime = load.tacticalInfo.estimatedPickup || load.tacticalInfo.scheduledPickupTime;
        // eslint-disable-next-line no-param-reassign
        load.deliveryTime = _.get(load, 'tacticalInfo.delivery.when', false) || load.tacticalInfo.estimatedDelivery;
      });

      return loads;
    },
    filterLoads(items, searchString, filter) {
      const search = searchString.toString().toLowerCase();
      this.$ga.event({
        eventCategory: 'search',
        eventAction: 'search-load',
        eventLabel: search,
      });

      window.__insp.push(['tagSession', 'search_booked_loads']);
      return items.filter(load =>
        filter(load.humanId, search) ||
        filter(load.sponsor.name, search) ||
        filter(this.loadModel.loadNickname(load), search) ||
        filter(this.loadModel.billOfLadingNumber(load), search) ||
        filter(this.loadModel.listingCompany(load).name, search) ||
        filter(this.loadModel.haulingCompany(load).name, search) ||
        filter(this.loadModel.pickupCompany(load).name, search) ||
        filter(this.loadModel.pickupLocationChute(load).companyName, search) ||
        filter(this.loadModel.pickupLocationChute(load).label, search) ||
        filter(this.loadModel.pickupLocationGooglePlace(load).placeName, search) ||
        filter(this.loadModel.pickupLocationGooglePlace(load).formattedAddress, search) ||
        filter(this.loadModel.dropoffCompany(load).name, search) ||
        filter(this.loadModel.dropoffLocationChute(load).companyName, search) ||
        filter(this.loadModel.dropoffLocationChute(load).label, search) ||
        filter(this.loadModel.dropoffLocationGooglePlace(load).placeName, search) ||
        filter(this.loadModel.dropoffLocationGooglePlace(load).formattedAddress, search) ||
        filter(this.loadModel.assignedTruckingCompanyDriverName(load), search));
    },
    filter(val, search) {
      return val != null && typeof val !== 'boolean' && val.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1;
    },
    w9Link(load) {
      return _.get(this.bookedLoadCarrierCompany(load), 'truckingInfo.w9Url', null);
    },
    async openW9File(load) {
      this.$store.commit(`applications/${types.START_LOADING}`);
      const response = await API.application.getDocument({ url: this.w9Link(load) });
      const url = window.URL.createObjectURL(response);
      window.open(url, '_self');
      this.$store.commit(`applications/${types.END_LOADING}`);
    },
    sortLoads(items, index, isDescending) {
      let sortedItems;
      switch (index) {
        case 'loaded-miles':
          sortedItems = _.sortBy(items, [load => _.get(load, 'tacticalInfo.loadDetails.loadedMiles', 0)]);
          break;
        case 'load-id':
          sortedItems = _.sortBy(items, [load => load.humanId]);
          break;
        case 'load-times':
          sortedItems = _.sortBy(items, [load =>
            moment(Model.load.notificationDeliveryTime(load).dateObject,
              moment.ISO_8601).unix()]);
          break;
        case 'carrier':
          sortedItems = _.sortBy(items, [load => this.bookedLoadHaulingCompany(load).name]);
          break;
        default:
          sortedItems = items;
      }

      this.$ga.event({
        eventCategory: 'sort',
        eventAction: 'sort-load',
        eventLabel: index,
      });

      localStorage.setItem('desktopLoadSortBy', index);

      if (isDescending) {
        sortedItems.reverse();

        localStorage.setItem('desktopLoadDescending', 'true');
      } else {
        localStorage.removeItem('desktopLoadDescending');
      }

      return sortedItems;
    },
    async checkIn(loadId) {
      this.checkinBtnLoading = true;
      try {
        const userPosition = await API.map.getUserPosition();
        await API.load.checkIn({
          loadId,
          lat: _.get(userPosition, 'latitude', null),
          lng: _.get(userPosition, 'longitude', null),
          timestamp: new Date().toISOString(),
        });
        this.$store.dispatch('applications/setSuccessNotification', {
          message: 'Checked In successfully!',
        });
      } catch (error) {
        this.$store.dispatch('applications/setErrorNotification', {
          message: error.message,
        });
      } finally {
        this.checkinBtnLoading = false;
      }
    },
    viewLoadDetail(load) {
      const loadId = _.get(load, 'humanId');
      this.$router.push(`/loads/${loadId}/detail`);
    },
    billOfLadingNumber(load) {
      return Loads.billOfLadingNumber(load);
    },
    listingCompany(load) {
      return Loads.listingCompany(load);
    },
    carrierCompany(load) {
      return Loads.carrierCompany(load);
    },
    assignedVendor(load) {
      return Loads.assignedVendor(load);
    },
    pickupLocation(load) {
      return Loads.pickupLocation(load);
    },
    dropoffLocation(load) {
      return Loads.dropoffLocation(load);
    },
    loadDetails(load) {
      return Loads.listingDetails(load);
    },
    driverId(load) {
      return Loads.assignedTruckingCompanyDriverUserId(load);
    },
    driverName(load) {
      return Loads.assignedTruckingCompanyDriverName(load);
    },
    hasOfficePermission(companyId) {
      const companyAccount = this.findCompanyAccount()(companyId);
      return this.isLtnAdmin || Checks.accountHasOfficePermission(companyAccount);
    },
    isDriver(load) {
      return Loads.isDriver(this.userProfile, load);
    },
    hasAssignedDriver(load) {
      return Loads.assignedTruckingCompanyDriverUserId(load);
    },
    isCarrierParticipant(load) {
      const carrier = this.bookedLoadCarrierCompany(load);
      return this.isDriver(load) ||
             this.isLtnAdmin ||
             (carrier && this.hasOfficePermission(carrier.id));
    },
    expandRow(slotItems) {
      // eslint-disable-next-line no-param-reassign
      slotItems.expanded = !slotItems.expanded;

      const listingCompanyId = this.loadModel.listingCompany(slotItems.item).id;
      if (slotItems.expanded &&
        this.hasBeenDelivered(slotItems.item) &&
        this.hasOfficePermission(listingCompanyId)) {
        this.loadingProfile = true;
        API.company.byId({ id: listingCompanyId }).then((companyProfile) => {
          this.loadingProfile = false;
          // eslint-disable-next-line no-param-reassign
          slotItems.item.billingAddress = _.get(companyProfile, 'data.producerInfo.billingAddress', {});
        });
      }
    },
    shouldShowRowExpandSlot(load) {
      const isProducerOffice = this.hasOfficePermission(Loads.listingCompany(load).id);
      const hasBillOfLadingNumber = this.billOfLadingNumber(load);
      return this.isLtnAdmin ||
        this.isCarrierParticipant(load) ||
        (isProducerOffice && hasBillOfLadingNumber);
    },
    isUnassigned(load) {
      const carrier = this.bookedLoadCarrierCompany(load);
      return carrier && this.hasOfficePermission(carrier.id)
        && !this.hasAssignedDriver(load);
    },
    isAssigned(load) {
      const carrier = this.bookedLoadCarrierCompany(load);
      return carrier && this.hasOfficePermission(carrier.id)
        && this.hasAssignedDriver(load);
    },
    hasBeenCanceled(load) {
      return load.loadStatus === loadStatus.CANCELED;
    },
    hasBeenDelivered(load) {
      return load.loadStatus === loadStatus.DELIVERED ||
             load.loadStatus === loadStatus.FINALIZED;
    },
    viewBillOfLading(load) {
      this.$store.dispatch('lightbox/show', load.billOfLading.link);
    },
    loadStyleClass(load) {
      return this.bookedLoadStyleClass(this.company, load);
    },
  },
  filters: {
    formatTimeStamp(momentDate) {
      return momentDate.format('HH:mm A MMM DD');
    },
  },
  watch: {
    loads(list) {
      this.normalizeLoads(list);
      this.loadsAssignedAt = moment();

      if (list) {
        const selectedId = _.get(this.selectedLoad, 'humanId') || this.selectedLoadId;
        this.updateSelectedLoadById(list, selectedId);
      }
    },
    selectedLoad(newVal) {
      if (_.get(newVal, 'humanId', false)) {
        this.$router.push({
          params: {
            bookedLoadId: newVal.humanId,
          },
        });
      }
    },
  },
  created() {
    const sortingValue = localStorage.getItem('desktopLoadSortBy');
    const descending = localStorage.getItem('desktopLoadDescending');
    if (sortingValue) {
      this.$set(this.pagination, 'sortBy', sortingValue);
      this.$set(this.pagination, 'descending', !!descending);
    }

    this.normalizeLoads(this.loads);

    const sectionKey = `${this.$route.name}_bookedLoadId`;

    if (this.viewedLoad) {
      this.selectedLoad = this.viewedLoad;
    } else if (this.selectedLoadId || localStorage.getItem(sectionKey)) {
      this.updateSelectedLoadById(this.loads,
        this.selectedLoadId || localStorage.getItem(sectionKey));
    }
  },
};
</script>

<style scoped>
  .scroll-section {
    height: calc(100vh - 225px);
    overflow: auto;
  }

  .pickup-col {
    width: 200px;
  }

  .id-col {
    position: relative;
    padding-left: 35px !important;
  }

  .expand-icon {
    position: absolute;
    left: 5px;
    top: auto;
    bottom: auto;
    margin-top: 10px;
  }

  .miles-col {
    width: 200px;
  }
</style>

<style>
  .load-id-header {
    padding-left: 35px !important;
  }

  .sort-btn {
    cursor: pointer;
  }
</style>



// WEBPACK FOOTER //
// src/components/dashboard/simple/ScheduledLoads.vue